import { useEffect, useRef, useState } from "react";
import type { GridAttributes, InputParam } from "../../types/grid.ts";
import { ParamType } from "../../types/grid.ts";
import type { ColumnDescription, InitCache } from "../../types/session.ts";
import { getFilter } from "./advancedFilters/registry.ts";
import styles from "./filterBar.module.scss";

// Text-input inline filter types. Other registered non-parameterOnly types
// (e.g. FilterComboBox / FilterCheckComboBox) also render inline — see
// hasInlineFilter / hasInlineComponent below.
export const INLINE_FILTER_TYPES = new Set([
  "filtertextboxlike",
  "filtertextboxlikeoptions",
  "filtertextbox",
]);

/** True when this FilterType is registered as a non-parameterOnly advanced
 *  filter component — it renders inline in the column header. */
function hasInlineComponent(filterType: string | undefined): boolean {
  if (!filterType) return false;
  const reg = getFilter(filterType);
  return !!reg && !reg.parameterOnly;
}

const LIKE_FILTER_TYPES = new Set([
  "filtertextboxlike",
  "filtertextboxlikeoptions",
]);

function stripLikeWrap(val: string): string {
  if (val.startsWith("%") && val.endsWith("%") && val.length >= 2) return val.slice(1, -1);
  return val;
}

/** Resolve the effective filter ColumnInfo for a column, following MigratedFilters if needed. */
function resolveFilterInfo(
  header: string,
  colInfoMap: Record<string, ColumnDescription>,
  migratedFilters?: Record<string, string | null> | null,
): ColumnDescription | undefined {
  const uc = header.toUpperCase();
  // If MigratedFilters maps this column to a source column, use that source's filter info
  if (migratedFilters && uc in migratedFilters) {
    const source = migratedFilters[uc];
    if (typeof source === "string") {
      return colInfoMap[source.toLowerCase()];
    }
    return undefined; // null = suppressed
  }
  return colInfoMap[header.toLowerCase()];
}

function hasInlineFilter(
  header: string,
  colInfoMap: Record<string, ColumnDescription>,
  hiddenFiltersSet: Set<string>,
  advancedColumnsSet?: Set<string>,
  migratedFilters?: Record<string, string | null> | null,
): boolean {
  const lc = header.toLowerCase();
  if (hiddenFiltersSet.has(lc)) return false;
  if (advancedColumnsSet?.has(lc)) return false;
  const info = resolveFilterInfo(header, colInfoMap, migratedFilters);
  if (!info?.FilterType) return false;
  const ft = info.FilterType.toLowerCase();
  return INLINE_FILTER_TYPES.has(ft) || hasInlineComponent(info.FilterType);
}

interface FilterBarProps {
  attributes: GridAttributes;
  filters: InputParam[];
  onChange: (filters: InputParam[]) => void;
  onSearch: () => void;
  displayNames?: Record<string, string>;
  colInfoMap: Record<string, ColumnDescription>;
  hiddenFiltersSet: Set<string>;
  advancedColumnsSet?: Set<string>;
  migratedFilters?: Record<string, string | null> | null;
  cache: InitCache | null;
}

export function FilterBar({
  attributes,
  filters,
  onChange,
  onSearch,
  displayNames,
  colInfoMap,
  hiddenFiltersSet,
  advancedColumnsSet,
  migratedFilters,
  cache,
}: FilterBarProps) {
  const [localValues, setLocalValues] = useState<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const f of filters) {
      map[f.sColumn] = stripLikeWrap(String(f.oValue ?? ""));
    }
    return map;
  });

  /* Resync only fires for *external* filter changes (clear, load saved
   * filter, parent-driven update) — not for our own onChange writes,
   * which would overwrite the user's literal text with the sanitised
   * oValue (stripping a trailing comma the user just typed, etc.). */
  const ownWriteRef = useRef(false);

  /* Debounce upstream onChange: each keystroke kept the input itself
   * snappy but flooded the parent with setFilters → sessionStorage write
   * → chip-strip rebuild → grid re-render. Comma in particular is the
   * heaviest keystroke (flips Type from Like to In, re-keys chips).
   * Local state stays immediate; upstream commits after the user pauses,
   * or eagerly on Enter (so search sees the latest value). */
  const DEBOUNCE_MS = 200;
  const pendingRef = useRef<Record<string, string> | null>(null);
  const timerRef = useRef<number | null>(null);

  const buildFilters = (values: Record<string, string>): InputParam[] =>
    Object.entries(values)
      .filter(([, v]) => v !== "")
      .map(([sColumn, oValue]) => {
        const info = resolveFilterInfo(sColumn, colInfoMap, migratedFilters);
        const filterType = info?.FilterType?.toLowerCase();
        const isLike = filterType && LIKE_FILTER_TYPES.has(filterType);
        // Comma in the input = list intent → emit ParamType.In, matching
        // the textBoxParamFilter behaviour. The chip strip auto-relabels
        // "contains" → "in" via the operator map. Trailing-comma input is
        // also treated as In (single-entry list); a comma-only input
        // yields zero parts and clears (filtered out by the v !== ""
        // guard already, but parts.length === 0 belt-and-braces).
        const trimmed = oValue.trim();
        if (trimmed.includes(",")) {
          const parts = trimmed.split(",").map((s) => s.trim()).filter(Boolean);
          if (parts.length > 0) {
            return { sColumn, oValue: parts.join(","), Type: ParamType.In };
          }
        }
        return {
          sColumn,
          oValue: isLike ? `%${oValue}%` : oValue,
          Type: isLike ? ParamType.Like : undefined,
        };
      });

  const flush = () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const next = pendingRef.current;
    if (!next) return;
    pendingRef.current = null;
    ownWriteRef.current = true;
    onChange(buildFilters(next));
  };

  useEffect(() => {
    if (ownWriteRef.current) {
      ownWriteRef.current = false;
      return;
    }
    const map: Record<string, string> = {};
    for (const f of filters) {
      map[f.sColumn] = stripLikeWrap(String(f.oValue ?? ""));
    }
    setLocalValues(map);
  }, [filters]);

  useEffect(() => () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
  }, []);

  const handleChange = (column: string, value: string) => {
    const updated = { ...localValues, [column]: value };
    setLocalValues(updated);
    pendingRef.current = updated;
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(flush, DEBOUNCE_MS);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      flush();
      onSearch();
    }
  };

  // Merge a single column's emitted InputParam[] into the global filters list.
  // Strips any existing entries for that column (matching by sColumn,
  // case-insensitive) and appends the new ones. Used by component-mode inline
  // filters (Combo, etc.) whose value shape doesn't fit the localValues map.
  const replaceColumnFilters = (column: string, next: InputParam[]) => {
    flush(); // commit any pending text edits first so we don't lose them
    const lc = column.toLowerCase();
    const kept = filters.filter((f) => f.sColumn.toLowerCase() !== lc);
    ownWriteRef.current = true;
    onChange([...kept, ...next]);
  };

  /* A column-bound filter counts as "filled" when it still carries a real
   * value — used to mark the header cell so a filter left in the fields (e.g.
   * one that survived an endpoint switch) can't hide off-screen. Unused is the
   * explicitly-cleared marker, so it never counts. */
  const hasActiveValue = (columnName: string): boolean =>
    filters.some((f) =>
      f.sColumn.replace(/^@/, "").toLowerCase() === columnName.toLowerCase()
      && f.Type !== ParamType.Unused
      && f.oValue != null
      && String(f.oValue) !== ""
      && String(f.oValue) !== "%%",
    );

  return (
    <tr>
      {attributes.header.map((header) => {
        const info = resolveFilterInfo(header, colInfoMap, migratedFilters);
        const ft = info?.FilterType?.toLowerCase() ?? "";
        const isInline = hasInlineFilter(header, colInfoMap, hiddenFiltersSet, advancedColumnsSet, migratedFilters);
        const useComponent = isInline && info && !INLINE_FILTER_TYPES.has(ft) && hasInlineComponent(info.FilterType);
        const reg = useComponent && info ? getFilter(info.FilterType) : null;
        const Component = reg?.component;
        const textValue = localValues[header] ?? "";
        const filled = !!isInline
          && (useComponent && info ? hasActiveValue(info.ColumnName) : textValue !== "");
        return (
          <th
            key={header}
            className={`${styles.filterCell}${filled ? ` ${styles.filterCellActive}` : ""}`}
            /* Reveal targets: the active-filter strip scrolls a filled filter
               back into view by column name. Both keys are indexed because a
               migrated filter emits its *source* column, not the header's. */
            data-filter-col={header.toLowerCase()}
            data-filter-src={info?.ColumnName?.toLowerCase()}
          >
            {Component && info && cache ? (
              <Component
                info={info}
                isParameter={false}
                value={filters.filter((f) => f.sColumn.toLowerCase() === info.ColumnName.toLowerCase())}
                onChange={(v) => replaceColumnFilters(info.ColumnName, v)}
                onSearch={onSearch}
                cache={cache}
                inline
              />
            ) : isInline && (
              <input
                type="text"
                value={textValue}
                onChange={(e) => handleChange(header, e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={displayNames?.[header.toLowerCase()]?.replace(/\\n/, " ") ?? header}
                title={textValue || undefined}
                className={`${styles.filterInput}${filled ? ` ${styles.filterInputFilled}` : ""}`}
                aria-label={`Filter by ${displayNames?.[header.toLowerCase()]?.replace(/\\n/, " ") ?? header}`}
              />
            )}
          </th>
        );
      })}
    </tr>
  );
}
